// Revenue Calculator — de reseller-menukaart "vanaf 01/01/2026" als rekenmodel.
//
// Nagebouwd (11-09-2026) van de Reseller Menu Card Calculator (Infinite Énergie);
// uitgebreid (24-09-2026) tot een demo-tool met drie scenario's (worst / base /
// best case) en een prognose over vijf jaar. Per productlijn geeft de partner
// zijn eigen parameters in (vaste vergoeding, indexfactor, opslag, volume, nieuwe
// EAN's per jaar) en ziet meteen de labelmarge per EAN per jaar en de
// partneropbrengst voor jaar 1 t.e.m. 5.
//
// Wat VAST ligt: alleen de kostopbouw per lijn (leveranciersmarge, sourcing,
// onbalans, minimale profileringsfactor — RC_LOCK). Alle globale parameters
// (marktprijzen, margeaandeel, vaste kost per maand, groei en churn) zijn per
// scenario bewerkbaar. Alles hier is pure rekenlogica, getest in
// revenueCalculator.test.js tegen de cijfers van de menukaart.
//
// Formules (letters = kolommen van de menukaart):
//   partner-energiemarge (€/MWh)
//     elektriciteit afname : (K − leveranciersmarge − sourcing − onbalans) + (J − minProf) × marktprijs EL
//     elektriciteit injectie: (|K| − leveranciersmarge − sourcing − onbalans) + (minProf − J) × marktprijs EL
//                             (GV rekent met |K|; B2C met −K — beide = de grootte van de opslag)
//     gas                  : (K − leveranciersmarge − sourcing) + (J − minProf) × marktprijs gas
//   labelmarge M (€/EAN/jaar) = (L × energiemarge + (I − vaste kost × 12)) × aandeel
//     (de vaste kost staat in €/maand op het scherm; de menukaart rekende met 30 €/jaar = 2,5 × 12)
//   EAN-bestand per lijn, jaar t = 1..5:
//     instroom_t = N × (1 + groei)^(t−1)
//     actief_1   = instroom_1 ; actief_t = actief_{t−1} × (1 − churn) + instroom_t
//     opbrengst_t = M × actief_t
//   Bij groei = churn = 0 is actief_t = N × t, precies de menukaart (jaar 2 = 2 × jaar 1,
//   jaar 3 = 3 × jaar 1: een constante instroom die zich opstapelt).
import { MENUKAART, brpCostForLabel, staffelVoordeel } from './menukaart';

// Vaste kostopbouw per lijn (menukaart, linkerblok B–D). NIET bewerkbaar en
// volledig afgeleid uit src/menukaart.js — dezelfde bron als de Revenue-
// berekening en het Menukaart-paneel. `sourcing` = de BRP-kost: B2B en
// grootverbruik altijd het lage tarief; B2C hangt af van het energielabel
// (rcLock(label)), zonder label het hoge tarief (= een nieuw label).
export function rcLock(label) {
  const el = MENUKAART.el, gas = MENUKAART.gas;
  const brpB2C = label ? brpCostForLabel(label) : el.brp.hoog;
  return {
    gv_el: { etMargin: el.marge, sourcing: el.brp.laag, imbalance: el.onbalansAfname.gvAmr, minProf: el.minProfilering },
    gv_gas: { etMargin: gas.marge, sourcing: gas.shipper, minProf: el.minProfilering },
    b2b_el_off: { etMargin: el.marge, sourcing: el.brp.laag, imbalance: el.onbalansAfname.rlp, minProf: el.minProfilering },
    b2b_el_smr: { etMargin: el.marge, sourcing: el.brp.laag, imbalance: el.onbalansAfname.smr3, minProf: el.minProfilering },
    b2b_gas: { etMargin: gas.marge, sourcing: gas.shipper, minProf: el.minProfilering },
    b2c_el_off: { etMargin: el.marge, sourcing: brpB2C, imbalance: el.onbalansAfname.rlp, minProf: el.minProfilering },
    b2c_el_smr: { etMargin: el.marge, sourcing: brpB2C, imbalance: el.onbalansAfname.smr3, minProf: el.minProfilering },
    b2c_gas: { etMargin: gas.marge, sourcing: gas.shipper, minProf: el.minProfilering },
  };
}
export const RC_LOCK = rcLock(null);

// Aantal prognosejaren.
export const RC_JAREN = 5;

// Globale parameters (per scenario bewerkbaar). vastGV/vastKV in €/maand
// (menukaart: 30 €/EAN/jaar = 2,5 €/maand); groei/churn in % per jaar;
// margeGvEl/margeGvGas = de leveranciersmarge voor grootverbruik (€/MWh), de
// enige menukaartkost die de partner zelf mag bijstellen (25-09-2026).
// `share` (margeaandeel, %) komt uit de standaardwaarden van het type (per
// scenario in te stellen door de beheerder); op het rekenscherm mag alleen een
// admin ervan afwijken.
export const RC_GLOBAL_DEFAULTS = {
  elMkt: MENUKAART.marktprijs.el, gasMkt: MENUKAART.marktprijs.gas, share: 75,
  vastGV: MENUKAART.vastrecht.basisPerJaar / 12, vastKV: MENUKAART.vastrecht.basisPerJaar / 12, groei: 0, churn: 0,
  margeGvEl: MENUKAART.el.marge, margeGvGas: MENUKAART.gas.marge,
};
export const RC_GLOBAL_FIELDS = ['elMkt', 'gasMkt', 'vastGV', 'vastKV', 'groei', 'churn', 'margeGvEl', 'margeGvGas'];
// Alle globale velden van de standaardwaarden: het margeaandeel hoort er daar bij.
export const RC_CONFIG_G_FIELDS = ['share', ...RC_GLOBAL_FIELDS];

// Twee types standaardwaarden (27-09-2026). Elke rol rekent met de standaard-
// waarden van zijn type: verkoper, teamleader en prospect-reseller met die van
// de wederverkoper, alle andere rollen (admin, dossierbeheerder, master,
// prospect-label) met die van het energielabel. Alleen admin en
// dossierbeheerder mogen op het scherm van type wisselen. `share` = het
// margeaandeel (%) zolang de beheerder er in de configuratie niets anders zet.
// SPIEGELT backend/src/util/rcConfig.js (TYPES, typeVoorRol).
export const RC_SHARE_75_ROLES = ['reseller', 'teamleader', 'prospect_reseller'];
export const RC_TYPES = [
  { id: 'label', label: 'Energielabel', share: 100 },
  { id: 'reseller', label: 'Wederverkoper', share: 75 },
];
export const RC_TYPE_IDS = RC_TYPES.map((t) => t.id);
export const rcType = (id) => RC_TYPES.find((t) => t.id === id) || RC_TYPES[0];
export const rcTypeVoorRol = (role) => (RC_SHARE_75_ROLES.includes(role) ? 'reseller' : 'label');
export const rcMagTypeWisselen = (role) => role === 'admin' || role === 'et_dossier';
export const rcShareVoorRol = (role) => rcType(rcTypeVoorRol(role)).share;

// De drie scenario's. Ze starten alle drie met de menukaartwaarden; alleen groei
// en churn verschillen. De reekskleur per case staat in App.jsx (RC_CASE_KLEUR,
// uit het gevalideerde CHART_HUES-palet).
export const RC_CASES = [
  { id: 'worst', label: 'Worst case', kort: 'Worst', groei: 0, churn: 15 },
  { id: 'base', label: 'Base case', kort: 'Base', groei: 10, churn: 5 },
  { id: 'best', label: 'Best case', kort: 'Best', groei: 25, churn: 5 },
];
export const RC_CASE_IDS = RC_CASES.map((c) => c.id);
export const rcCase = (id) => RC_CASES.find((c) => c.id === id) || RC_CASES[1];

// Productlijnen per segment — één op één de rijen van de menukaart, met de
// standaardwaarden van de gele velden: I (vaste vergoeding €/EAN/jaar), J
// (indexfactor), K (opslag €/MWh), L (volume MWh/jaar), N (nieuwe EAN's/jaar).
export const RC_SEGMENTS = {
  GV: {
    title: 'Grootverbruik (GV)', sub: 'AMR/MMR-meting',
    lines: [
      { id: 'gv_el_off', name: 'Elektriciteit — afname (AMR)', tag: 'EL_GV · afname', energy: 'el', dir: 'off', lock: 'gv_el', base: 'GV', I: 120, J: 1, K: 12, L: 350, N: 0 },
      { id: 'gv_el_inj', name: 'Elektriciteit — injectie', tag: 'EL_GV · injectie', energy: 'el', dir: 'inj', lock: 'gv_el', base: 'GV', I: 0, J: 1, K: -15, L: 100, N: 0 },
      { id: 'gv_gas', name: 'Gas (YMR, MMR)', tag: 'GAS_GV · afname', energy: 'gas', dir: 'off', lock: 'gv_gas', base: 'GV', I: 120, J: 1, K: 12, L: 250, N: 0 },
    ],
  },
  B2B: {
    title: 'Professioneel (KV_B2B)', sub: 'Klein zakelijk · RLP / SMR3',
    lines: [
      { id: 'b2b_el_rlp', name: 'Elektriciteit — afname RLP (variabel)', tag: 'EL_KV_B2B · RLP', energy: 'el', dir: 'off', lock: 'b2b_el_off', base: 'KV', I: 96, J: 1, K: 18, L: 10, N: 0 },
      { id: 'b2b_el_smr', name: 'Elektriciteit — afname SMR3 (dynamisch)', tag: 'EL_KV_B2B · SMR3', energy: 'el', dir: 'off', lock: 'b2b_el_smr', base: 'KV', I: 96, J: 1, K: 8, L: 350, N: 0 },
      { id: 'b2b_gas', name: 'Gas', tag: 'GAS_KV_B2B', energy: 'gas', dir: 'off', lock: 'b2b_gas', base: 'KV', I: 96, J: 1.05, K: 10, L: 20, N: 0 },
    ],
  },
  B2C: {
    title: 'Particulier (KV_B2C)', sub: 'Residentieel · RLP / SMR3',
    lines: [
      { id: 'b2c_el_rlp', name: 'Elektriciteit — afname RLP (variabel)', tag: 'EL_KV_B2C · RLP', energy: 'el', dir: 'off', lock: 'b2c_el_off', base: 'KV', I: 96, J: 1.05, K: 15, L: 3.5, N: 1 },
      { id: 'b2c_el_smr', name: 'Elektriciteit — afname SMR3 (dynamisch)', tag: 'EL_KV_B2C · SMR3', energy: 'el', dir: 'off', lock: 'b2c_el_smr', base: 'KV', I: 96, J: 1.05, K: 15, L: 3.5, N: 0 },
      { id: 'b2c_gas', name: 'Gas', tag: 'GAS_KV_B2C', energy: 'gas', dir: 'off', lock: 'b2c_gas', base: 'KV', I: 96, J: 1.05, K: 10, L: 17, N: 1 },
    ],
  },
};
export const RC_ORDER = ['GV', 'B2B', 'B2C'];
export const RC_FIELDS = ['I', 'J', 'K', 'L', 'N'];

const num0 = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? 0 : n; };

// Grenzen van de indexfactor: afname 1 – 1,4 ; injectie 0,5 – 1.
export const RC_J_GRENS = { off: { min: 1, max: 1.4 }, inj: { min: 0.5, max: 1 } };
export const jGrens = (line) => RC_J_GRENS[line && line.dir === 'inj' ? 'inj' : 'off'];

// Partner-energiemarge (€/MWh) van één lijn. `lock` = de kostopbouw (rcLock(label)).
export function resellerMargin(line, K, J, elMkt, gasMkt, lock = RC_LOCK) {
  const c = lock[line.lock];
  if (line.energy === 'gas') return (K - c.etMargin - c.sourcing) + (J - c.minProf) * gasMkt;
  if (line.dir === 'inj') {
    const kAbs = line.lock === 'gv_el' ? Math.abs(K) : -K;
    const imb = line.imbInj != null ? line.imbInj : c.imbalance;
    return (kAbs - c.etMargin - c.sourcing - imb) + (c.minProf - J) * elMkt;
  }
  return (K - c.etMargin - c.sourcing - c.imbalance) + (J - c.minProf) * elMkt;
}

// Actieve EAN's per jaar (1..RC_JAREN) voor één lijn met N nieuwe EAN's in jaar 1,
// een groei van de instroom en een churn op het bestand (beide in %).
export function actieveEans(N, groeiPct, churnPct, jaren = RC_JAREN) {
  const n = num0(N), groei = num0(groeiPct) / 100, churn = Math.min(1, Math.max(0, num0(churnPct) / 100));
  const uit = [];
  let actief = 0;
  for (let t = 1; t <= jaren; t += 1) {
    const instroom = n * Math.pow(1 + groei, t - 1);
    actief = t === 1 ? instroom : actief * (1 - churn) + instroom;
    uit.push(actief);
  }
  return uit;
}

// De kostopbouw van een lijn zoals de berekening ze gebruikt: de menukaart, met
// voor grootverbruik de leveranciersmarge uit de globale parameters van het
// scenario (margeGvEl / margeGvGas) — de enige kost die de partner mag bijstellen.
export function rcLockVoorLijn(line, g, lock = RC_LOCK) {
  const c = lock[line.lock];
  if (line.lock === 'gv_el' && g && g.margeGvEl !== undefined && g.margeGvEl !== null && g.margeGvEl !== '') return { ...c, etMargin: num0(g.margeGvEl) };
  if (line.lock === 'gv_gas' && g && g.margeGvGas !== undefined && g.margeGvGas !== null && g.margeGvGas !== '') return { ...c, etMargin: num0(g.margeGvGas) };
  return c;
}

// Labelmarge en opbrengst van één lijn. `g` = globale parameters (share in %,
// vaste kost in €/maand, groei/churn in %), `lock` = kostopbouw (rcLock(label)).
// `mwh` = het geleverde volume per jaar (volume per EAN × actieve EAN's).
export function calcLine(line, g, lock = RC_LOCK) {
  const vastJaar = (line.base === 'GV' ? num0(g.vastGV) : num0(g.vastKV)) * 12;
  const share = num0(g.share) / 100;
  const lk = { ...lock, [line.lock]: rcLockVoorLijn(line, g, lock) };
  const margin = resellerMargin(line, num0(line.K), num0(line.J), num0(g.elMkt), num0(g.gasMkt), lk);
  const M = (num0(line.L) * margin + (num0(line.I) - vastJaar)) * share;
  const actief = actieveEans(line.N, g.groei, g.churn);
  const jaren = actief.map((a) => M * a);
  const mwh = actief.map((a) => num0(line.L) * a);
  // y1/y2/y3 blijven bestaan voor de menukaart-vergelijking en oudere aanroepers.
  return { margin, M, actief, jaren, mwh, y1: jaren[0], y2: jaren[1], y3: jaren[2] };
}

// Validatie van de invoervelden. Geeft een tekst terug bij een fout, anders null.
//   I ≥ 0 ; J binnen de grens van de lijn ; K vrij ; L ≥ 0 ; N geheel getal ≥ 0 ;
//   share 0 < x ≤ 100 ; elMkt/gasMkt > 0 ; vastGV/vastKV ≥ 0 ; groei ≥ 0 ; churn 0–100.
// `line` is enkel nodig voor J (afname of injectie).
export function validateField(field, value, line) {
  if (value === '' || value == null) return field === 'K' ? null : 'Verplicht veld';
  if (Number.isNaN(Number(value))) return 'Geen geldig getal';
  const n = Number(value);
  if (field === 'I' && n < 0) return 'Vaste vergoeding moet ≥ 0 zijn';
  if (field === 'J') {
    const gr = jGrens(line);
    if (n <= 0) return 'Indexfactor moet groter zijn dan 0';
    if (n < gr.min || n > gr.max) return `Indexfactor ${line && line.dir === 'inj' ? 'injectie' : 'afname'}: tussen ${String(gr.min).replace('.', ',')} en ${String(gr.max).replace('.', ',')}`;
  }
  if (field === 'L' && n < 0) return 'Volume moet ≥ 0 zijn';
  if (field === 'N' && (!Number.isInteger(n) || n < 0)) return 'Nieuwe EAN\'s moet een geheel getal ≥ 0 zijn';
  if (field === 'share' && (n <= 0 || n > 100)) return 'Margeaandeel moet tussen 0 en 100 % liggen';
  if ((field === 'elMkt' || field === 'gasMkt') && n <= 0) return 'Marktprijs moet groter zijn dan 0';
  if ((field === 'vastGV' || field === 'vastKV') && n < 0) return 'Vaste kost moet ≥ 0 zijn';
  if (field === 'groei' && n < 0) return 'Groei moet ≥ 0 zijn';
  if (field === 'churn' && (n < 0 || n > 100)) return 'Churn moet tussen 0 en 100 % liggen';
  if ((field === 'margeGvEl' || field === 'margeGvGas') && n < 0) return 'Leveranciersmarge moet ≥ 0 zijn';
  return null;
}

const somJaren = (a, b) => a.map((v, i) => v + (b[i] || 0));
const nulJaren = () => Array.from({ length: RC_JAREN }, () => 0);

// Alles in één keer: per segment de lijnen met hun uitkomst en het segmenttotaal,
// plus de samenvatting over alle segmenten (totaal nieuwe EAN's, jaar 1–5 per
// jaar, cumulatief, actieve EAN's per jaar). `segments` heeft de vorm van
// RC_SEGMENTS (met de huidige waarden).
export function calcAll(segments, g, lock = RC_LOCK) {
  const perSegment = {};
  let totalEan = 0, jaren = nulJaren(), actief = nulJaren(), mwh = nulJaren();
  for (const key of RC_ORDER) {
    const seg = segments[key];
    if (!seg) continue;
    const lines = seg.lines.map((line) => ({ line, ...calcLine(line, g, lock) }));
    const segJaren = lines.reduce((a, r) => somJaren(a, r.jaren), nulJaren());
    const segActief = lines.reduce((a, r) => somJaren(a, r.actief), nulJaren());
    const segMwh = lines.reduce((a, r) => somJaren(a, r.mwh), nulJaren());
    perSegment[key] = { lines, total: { jaren: segJaren, actief: segActief, mwh: segMwh, y1: segJaren[0], y2: segJaren[1], y3: segJaren[2] } };
    totalEan += seg.lines.reduce((a, l) => a + num0(l.N), 0);
    jaren = somJaren(jaren, segJaren);
    actief = somJaren(actief, segActief);
    mwh = somJaren(mwh, segMwh);
  }
  // Staffelkorting op het vastrecht (contract 6.6.1, src/menukaart.js): boven de
  // eerste schijf betaalt de partner per EAN minder vastrecht aan de leverancier.
  // Dat voordeel hangt af van het TOTALE aantal actieve EAN's over alle segmenten
  // en komt daarom hier bij, niet per lijn; het partneraandeel (share) geldt ook
  // hierop. Zonder korting (≤ 2.000 EAN's) verandert er niets aan de cijfers.
  const share = num0(g.share) / 100;
  const staffelPerJaar = actief.map((a) => staffelVoordeel(a));
  const staffel = staffelPerJaar.map((s) => s.voordeel * share);
  const jarenZonderStaffel = jaren;
  jaren = somJaren(jaren, staffel);
  const cumulative = jaren.reduce((a, v) => a + v, 0);
  return {
    perSegment, totalEan, jaren, actief, mwh, actiefEind: actief[RC_JAREN - 1], y1: jaren[0], y2: jaren[1], y3: jaren[2], cumulative, cumulative3: jaren[0] + jaren[1] + jaren[2],
    jarenZonderStaffel,
    staffel: { voordeel: staffel, cumulatief: staffel.reduce((a, v) => a + v, 0), detail: staffelPerJaar, actief: staffel.some((v) => v > 0), drempel: staffelPerJaar[0].drempel },
  };
}

// Diepe kopie van de productlijnen met de waarden van de menukaart.
export const rcDefaults = () => JSON.parse(JSON.stringify(RC_SEGMENTS));

// ── Standaardwaarden, persoonlijke afwijkingen en favorieten (27-09-2026) ────
// Drie lagen, van onder naar boven:
//   1. FABRIEK   — de menukaart in deze code (RC_SEGMENTS, RC_GLOBAL_DEFAULTS,
//                  RC_CASES) met het margeaandeel van het type;
//   2. STANDAARD — wat de beheerder per type (Energielabel / Wederverkoper)
//                  instelt op de Admin-pagina; bewaard wordt alleen wat van de
//                  fabriek afwijkt, zodat een latere menukaartwijziging doorwerkt
//                  in alles wat de beheerder niet zelf heeft gezet;
//   3. PERSOONLIJK — wat de gebruiker op zijn rekenscherm wijzigt; ook hier
//                  alleen de velden die van de standaard afwijken. Wie één veld
//                  aanpast, volgt voor al het andere de standaard van de beheerder.
// Eén vorm voor alle drie, de "waardenset":
//   { cases: { worst|base|best: { g: { veld: waarde }, lines: { lijnId: { I, J, K, L, N } } } } }
// Een favoriet is een VOLLEDIGE waardenset (alle velden): die moet dezelfde
// cijfers geven, ook nadat de beheerder de standaard wijzigt.
const alleLijnen = () => RC_ORDER.flatMap((k) => RC_SEGMENTS[k].lines);
export const RC_LINE_IDS = alleLijnen().map((l) => l.id);
const isGetal = (v) => v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v));
// Wat in een invoerveld mag staan: een getal of een niet-lege tekst (ook een
// tussenstand als "2." tijdens het typen). Al het andere wordt genegeerd.
const isInvoer = (v) => (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.trim() !== '');
const wijktAf = (v, basis) => isInvoer(v) && String(v) !== String(basis);
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export function rcFabriek(type) {
  const t = rcType(type);
  const cases = {};
  for (const c of RC_CASES) {
    const bron = { ...RC_GLOBAL_DEFAULTS, share: t.share, groei: c.groei, churn: c.churn };
    const lines = {};
    for (const line of alleLijnen()) lines[line.id] = Object.fromEntries(RC_FIELDS.map((f) => [f, line[f]]));
    cases[c.id] = { g: Object.fromEntries(RC_CONFIG_G_FIELDS.map((f) => [f, bron[f]])), lines };
  }
  return { cases };
}

// Een waardenset over een basis leggen. Alleen bekende cases, lijnen en velden
// tellen; `neem(v)` beslist welke waarden meedoen, `zet(v)` hoe ze bewaard worden.
function legOver(basis, set, { neem = isInvoer, zet = (v) => v, zonder = [] } = {}) {
  const uit = JSON.parse(JSON.stringify(basis));
  const bron = isObject(set) && isObject(set.cases) ? set.cases : {};
  for (const id of RC_CASE_IDS) {
    const c = bron[id];
    if (!isObject(c)) continue;
    const g = isObject(c.g) ? c.g : {};
    for (const f of RC_CONFIG_G_FIELDS) if (!zonder.includes(f) && neem(g[f])) uit.cases[id].g[f] = zet(g[f]);
    const lines = isObject(c.lines) ? c.lines : {};
    for (const lineId of RC_LINE_IDS) {
      if (!isObject(lines[lineId])) continue;
      for (const f of RC_FIELDS) if (neem(lines[lineId][f])) uit.cases[id].lines[lineId][f] = zet(lines[lineId][f]);
    }
  }
  return uit;
}

// Wat in `set` afwijkt van `basis`, als (onvolledige) waardenset. `getal: true`
// vergelijkt en bewaart als getal (de configuratie van de beheerder); anders
// blijft de invoer letterlijk staan (het rekenscherm, ook tijdens het typen).
export function rcSetVerschil(set, basis, { getal = false, zonder = [] } = {}) {
  const anders = getal ? (v, b) => isGetal(v) && Number(v) !== Number(b) : wijktAf;
  const vorm = getal ? Number : (v) => v;
  const cases = {};
  for (const id of RC_CASE_IDS) {
    const c = set.cases[id], b = basis.cases[id];
    const g = {};
    for (const f of RC_CONFIG_G_FIELDS) if (!zonder.includes(f) && anders(c.g[f], b.g[f])) g[f] = vorm(c.g[f]);
    const lines = {};
    for (const lineId of RC_LINE_IDS) {
      const d = {};
      for (const f of RC_FIELDS) if (anders(c.lines[lineId][f], b.lines[lineId][f])) d[f] = vorm(c.lines[lineId][f]);
      if (Object.keys(d).length) lines[lineId] = d;
    }
    if (Object.keys(g).length || Object.keys(lines).length) cases[id] = { g, lines };
  }
  return { cases };
}
export const rcAantalVelden = (set) => Object.values((set && set.cases) || {}).reduce((n, c) =>
  n + Object.keys(c.g || {}).length + Object.values(c.lines || {}).reduce((m, l) => m + Object.keys(l).length, 0), 0);

// Standaardwaarden van een type: de fabriek met daaroverheen wat de beheerder
// bewaarde (`config` = waardenset uit /api/revenue-calculator/config, of null).
export const rcStandaard = (type, config) => legOver(rcFabriek(type), config, { neem: isGetal, zet: Number });
// Wat de beheerder bewaart: alleen wat van de fabriek afwijkt, als getallen.
export const rcConfigVoorOpslag = (set, type) => rcSetVerschil(set, rcFabriek(type), { getal: true });

// Bewaarde scenario's van vóór 27-09-2026 droegen ALLE waarden, zonder versie:
//   { actief, share?, cases: { worst: { g, lines }, … } }   of nog ouder   { share, lines }
// (dat laatste wordt de base case). Toen was de standaard de menukaart, dus wat
// daarvan afwijkt is wat de gebruiker zelf heeft ingevuld. `share` stond
// bovenaan en gold voor alle scenario's.
function uitOudeVorm(saved) {
  const heeftCases = isObject(saved.cases);
  const vol = { cases: {} };
  for (const id of RC_CASE_IDS) {
    const bron = heeftCases ? saved.cases[id] : (id === 'base' ? saved : null);
    if (!isObject(bron)) continue;
    const sg = isObject(bron.g) ? bron.g : bron;
    const g = Object.fromEntries(RC_GLOBAL_FIELDS.filter((f) => isGetal(sg[f])).map((f) => [f, Number(sg[f])]));
    vol.cases[id] = { g, lines: isObject(bron.lines) ? bron.lines : {} };
  }
  const fabriek = rcFabriek('label');
  const uit = rcSetVerschil(legOver(fabriek, vol), fabriek, { zonder: ['share'] });
  if (isGetal(saved.share)) {
    for (const id of RC_CASE_IDS) {
      const c = uit.cases[id] || { g: {}, lines: {} };
      uit.cases[id] = { ...c, g: { ...c.g, share: Number(saved.share) } };
    }
  }
  return { v: 2, actief: heeftCases ? saved.actief : 'base', cases: uit.cases };
}
export function rcPersoonlijkNormaal(saved) {
  if (!isObject(saved)) return { v: 2, actief: 'base', cases: {} };
  if (saved.v === 2) return { v: 2, actief: saved.actief, cases: isObject(saved.cases) ? saved.cases : {} };
  return uitOudeVorm(saved);
}

// De staat van het rekenscherm: { actief, cases: { id: { segments, g } } }, met
// `segments` in de vorm van RC_SEGMENTS en `g` inclusief het margeaandeel.
// `persoonlijk` = wat de gebruiker bewaarde (afwijkingen, een favoriet of een
// oude vorm). `magShare`: alleen een admin mag van het margeaandeel afwijken.
export function rcStaat(standaard, persoonlijk, { magShare = false } = {}) {
  const p = rcPersoonlijkNormaal(persoonlijk);
  const set = legOver(standaard, p, { zonder: magShare ? [] : ['share'] });
  const cases = {};
  for (const id of RC_CASE_IDS) {
    const segments = rcDefaults();
    for (const key of RC_ORDER) for (const line of segments[key].lines) Object.assign(line, set.cases[id].lines[line.id]);
    cases[id] = { segments, g: set.cases[id].g };
  }
  return { actief: RC_CASE_IDS.includes(p.actief) ? p.actief : 'base', cases };
}

// Alle waarden van de staat als waardenset — de vorm van een favoriet en van de export.
export function rcSetVanStaat(state) {
  const cases = {};
  for (const id of RC_CASE_IDS) {
    const c = state.cases[id];
    const lines = {};
    for (const key of RC_ORDER) for (const line of c.segments[key].lines) lines[line.id] = Object.fromEntries(RC_FIELDS.map((f) => [f, line[f]]));
    cases[id] = { g: Object.fromEntries(RC_CONFIG_G_FIELDS.map((f) => [f, c.g[f]])), lines };
  }
  return { cases };
}
// Wat per gebruiker bewaard wordt (ui-prefs): alleen de afwijkingen van de standaard.
export function rcPersoonlijkVan(state, standaard, { magShare = false } = {}) {
  return { v: 2, actief: state.actief, ...rcSetVerschil(rcSetVanStaat(state), standaard, { zonder: magShare ? [] : ['share'] }) };
}
// Een favoriet: alle waarden, plus het type waaronder hij gemaakt is.
export const rcFavorietVan = (state, type) => ({ v: 2, type, actief: state.actief, ...rcSetVanStaat(state) });

// ── De parameters als tabel (Admin-pagina en export) ─────────────────────────
// Naam, eenheid en toelichting per veld: één bron voor het rekenscherm en de
// configuratietabel.
export const RC_PARAM_INFO = {
  share: { kop: 'Margeaandeel partner', eenheid: '%', tip: 'Het deel van de labelmarge dat naar de partner gaat.' },
  elMkt: { kop: 'Marktprijs elektriciteit', eenheid: '€/MWh', tip: 'Referentiemarktprijs elektriciteit. Telt mee via de indexfactor: (indexfactor − 1) × marktprijs.' },
  gasMkt: { kop: 'Marktprijs gas', eenheid: '€/MWh', tip: 'Referentiemarktprijs gas. Telt mee via de indexfactor: (indexfactor − 1) × marktprijs.' },
  groei: { kop: "Jaarlijkse groei nieuwe EAN's", eenheid: '%/jaar', tip: 'Hoeveel meer nieuwe EAN\'s je elk jaar aanbrengt dan het jaar ervoor. Jaar 2 = jaar 1 × (1 + groei), enzovoort.' },
  churn: { kop: 'Jaarlijkse churn', eenheid: '%/jaar', tip: 'Welk deel van de actieve EAN\'s je elk jaar verliest. Actieve EAN\'s = vorig jaar × (1 − churn) + nieuwe instroom.' },
  vastGV: { kop: 'Vaste kost', eenheid: '€/EAN/maand', tip: 'Vaste kost per EAN per maand voor grootverbruik (menukaart: 2,5 €/maand = 30 €/jaar). De berekening is per jaar en rekent met × 12. Wat daarboven als vaste vergoeding wordt aangerekend, is marge.' },
  vastKV: { kop: 'Vaste kost', eenheid: '€/EAN/maand', tip: 'Vaste kost per EAN per maand voor klein verbruik (menukaart: 2,5 €/maand = 30 €/jaar). Professioneel en particulier delen deze vaste kost; de berekening rekent met × 12.' },
  margeGvEl: { kop: 'Leveranciersmarge elektriciteit', eenheid: '€/MWh', tip: 'Marge van de leverancier per MWh elektriciteit voor grootverbruik, afname en injectie. Een lagere marge laat meer over voor de partner.' },
  margeGvGas: { kop: 'Leveranciersmarge gas', eenheid: '€/MWh', tip: 'Marge van de leverancier per MWh gas voor grootverbruik. Een lagere marge laat meer over voor de partner.' },
  I: { kop: 'Vaste vergoeding', eenheid: '€/EAN/jaar', tip: 'Jaarlijkse vaste vergoeding die de klant betaalt per EAN. Het deel boven de vaste kost (€/maand × 12) is marge.' },
  J: { kop: 'Indexfactor', eenheid: '×', tip: 'Vermenigvuldiger op de marktprijs in de prijsformule van de klant. Afname: tussen 1 en 1,4 — boven 1 levert dat energiemarge op. Injectie: tussen 0,5 en 1 — onder 1 levert marge op.' },
  K: { kop: 'Opslag', eenheid: '€/MWh', tip: 'Vaste opslag per MWh in de prijsformule van de klant. Bij injectie negatief (de klant ontvangt minder dan de marktprijs).' },
  L: { kop: 'Volume', eenheid: 'MWh/jaar', tip: 'Verwacht jaarvolume per EAN.' },
  N: { kop: "Nieuwe EAN's", eenheid: 'per jaar', tip: 'Hoeveel nieuwe aansluitingen je in jaar 1 op deze lijn verwacht aan te brengen. De volgende jaren groeit dat aantal met het groeipercentage.' },
};
export const RC_SEGMENT_NAAM = { GV: 'Grootverbruik', B2B: 'Professioneel', B2C: 'Particulier' };

// Eén rij per parameter, in de volgorde van het rekenscherm. `groep` stuurt de
// groepering, `segmenten` de segmentfilter (de vaste kost klein verbruik hoort
// bij Professioneel én Particulier), `line` is nodig om de indexfactor te toetsen.
export function rcConfigRijen() {
  const rij = (groep, segmenten, type, energie, veld, line = null) => ({
    key: line ? `${line.id}.${veld}` : `g.${veld}`, groep, segmenten, type, energie, veld, lineId: line ? line.id : null, line, ...RC_PARAM_INFO[veld],
  });
  const uit = [
    rij('globaal', [], 'Globaal', null, 'share'), rij('globaal', [], 'Globaal', 'el', 'elMkt'), rij('globaal', [], 'Globaal', 'gas', 'gasMkt'),
    rij('globaal', [], 'Globaal', null, 'groei'), rij('globaal', [], 'Globaal', null, 'churn'),
    rij('GV', ['GV'], 'Grootverbruik – menukaart', null, 'vastGV'), rij('GV', ['GV'], 'Grootverbruik – menukaart', 'el', 'margeGvEl'), rij('GV', ['GV'], 'Grootverbruik – menukaart', 'gas', 'margeGvGas'),
    rij('KV', ['B2B', 'B2C'], 'Klein verbruik – menukaart', null, 'vastKV'),
  ];
  for (const key of RC_ORDER) for (const line of RC_SEGMENTS[key].lines) for (const f of RC_FIELDS) uit.push(rij(key, [key], `${RC_SEGMENT_NAAM[key]} – ${line.name.replace(' — ', ' ')}`, line.energy, f, line));
  return uit;
}
export const rcRijWaarde = (set, caseId, rij) => (rij.lineId ? set.cases[caseId].lines[rij.lineId][rij.veld] : set.cases[caseId].g[rij.veld]);
// Nieuwe waardenset met één gewijzigde waarde; de bron blijft onaangeroerd.
export function rcZetRijWaarde(set, caseId, rij, value) {
  const uit = JSON.parse(JSON.stringify(set));
  if (rij.lineId) uit.cases[caseId].lines[rij.lineId][rij.veld] = value; else uit.cases[caseId].g[rij.veld] = value;
  return uit;
}

// De volledige case `van` (gele velden + globale parameters) kopiëren naar `naar`.
// Geeft een nieuwe state terug; de bron blijft onaangeroerd.
export function rcKopieerCase(state, van, naar) {
  if (van === naar || !state.cases[van] || !state.cases[naar]) return state;
  const kopie = JSON.parse(JSON.stringify(state.cases[van]));
  return { ...state, cases: { ...state.cases, [naar]: kopie } };
}
